/**
 * Global Validation Queue Manager
 *
 * Manages a queue of validation requests to ensure only one store validates at a time.
 * This prevents overwhelming the server with simultaneous validation requests.
 * Uses the global validation manager to maintain state across component lifecycles.
 */

import globalValidationManager from './globalValidationManager.js';

class ValidationQueue {
    constructor() {
        this.queue = [];
        this.isProcessing = false;
        this.currentValidation = null;
        this.listeners = new Set();
    }

    /**
     * Add a store to the validation queue
     * @param {Object} store - Store object with name, pages, isTestStore, etc.
     * @param {Function} onProgress - Callback for progress updates (legacy, now handled by global manager)
     * @param {Function} onComplete - Callback when validation completes
     * @param {Function} onError - Callback for errors
     */
    addToQueue(store, onProgress, onComplete, onError) {
        // Check if this store is already in the queue
        const alreadyQueued = this.queue.some(item => item.store.name === store.name);
        if (alreadyQueued) {
            console.log(`Store ${store.name} is already queued`);
            return null;
        }

        // Check if this store is currently validating
        if (this.currentValidation && this.currentValidation.store.name === store.name) {
            console.log(`Store ${store.name} is already being validated`);
            return null;
        }

        const queueItem = {
            id: `${store.name}-${Date.now()}`,
            store,
            onComplete,
            onError,
            status: 'queued'
        };

        this.queue.push(queueItem);
        this.notifyListeners();

        // Start processing if not already running
        if (!this.isProcessing) {
            this.processQueue();
        }

        return queueItem.id;
    }

    /**
     * Process the validation queue
     */
    async processQueue() {
        if (this.isProcessing || this.queue.length === 0) {
            return;
        }

        this.isProcessing = true;

        while (this.queue.length > 0) {
            const item = this.queue.shift();
            this.currentValidation = item;
            item.status = 'processing';
            this.notifyListeners();

            try {
                // Use global validation manager to start validation
                await globalValidationManager.startValidation(item.store);

                // Call completion callback
                if (item.onComplete) {
                    item.onComplete();
                }
            } catch (error) {
                console.error('Validation error:', error);
                if (item.onError) {
                    item.onError(error);
                }
            }

            // Small delay between validations
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        this.currentValidation = null;
        this.isProcessing = false;
        this.notifyListeners();
    }

    /**
     * Get current queue status
     */
    getStatus() {
        return {
            queueLength: this.queue.length,
            isProcessing: this.isProcessing,
            currentValidation: this.currentValidation,
            queuedStores: this.queue.map(item => ({
                id: item.id,
                storeName: item.store.name,
                status: item.status
            }))
        };
    }

    /**
     * Add a listener for queue status changes
     */
    addListener(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    /**
     * Notify all listeners of queue status change
     */
    notifyListeners() {
        const status = this.getStatus();
        this.listeners.forEach(listener => listener(status));
    }

    /**
     * Clear the queue (emergency stop)
     */
    clearQueue() {
        this.queue = [];
        this.notifyListeners();
    }
}

// Create and export a singleton instance
const validationQueue = new ValidationQueue();

export default validationQueue;
